import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/sonner';

export const usePushNotifications = () => {
  useEffect(() => {
    const registerNotifications = async () => {
      // Only run in native mobile app environment
      if (!Capacitor.isNativePlatform()) {
        console.log('Push notifications only available in native mobile apps');
        return;
      }

      try {
        // Dynamic import to avoid loading in web environment
        const { PushNotifications } = await import('@capacitor/push-notifications');
        
        // Check current permission status
        let permStatus = await PushNotifications.checkPermissions();

        // Always request permissions if not explicitly granted
        if (permStatus.receive !== 'granted') {
          toast.info('Enable notifications to receive trading alerts and updates');
          permStatus = await PushNotifications.requestPermissions();
        }

        if (permStatus.receive !== 'granted') {
          console.log('Push notification permission denied');
          toast.warning('Notifications disabled. Enable them in your device settings to receive alerts.');
          return;
        }

        toast.success('Notifications enabled successfully');

        // Register with Apple / Google to receive push via APNS/FCM
        await PushNotifications.register();

        // Listen for registration success
        await PushNotifications.addListener('registration', async (token) => {
          console.log('Push registration success, token: ' + token.value);
          
          // Save token to database for this user
          const { data: { user } } = await supabase.auth.getUser();
          if (user) {
            await supabase
              .from('push_tokens')
              .upsert({ 
                user_id: user.id, 
                token: token.value,
                updated_at: new Date().toISOString()
              });
          }
        });

        // Listen for registration errors
        await PushNotifications.addListener('registrationError', (error) => {
          console.error('Error on registration: ' + JSON.stringify(error));
        });

        // Listen for push notifications received
        await PushNotifications.addListener(
          'pushNotificationReceived',
          (notification) => {
            console.log('Push notification received: ', notification);
            // You can show a notification here or update app state
          }
        );

        // Listen for push notification actions
        await PushNotifications.addListener(
          'pushNotificationActionPerformed',
          (notification) => {
            console.log('Push notification action performed', notification);
          }
        );

      } catch (error) {
        console.error('Error setting up push notifications:', error);
      }
    };

    registerNotifications();

    return () => {
      if (Capacitor.isNativePlatform()) {
        import('@capacitor/push-notifications').then(({ PushNotifications }) => {
          PushNotifications.removeAllListeners();
        });
      }
    };
  }, []);
};

